import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const CartContext = createContext(null);

const STORAGE_KEY = "ave_cart_v1";
const COUPON_KEY = "ave_coupon_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [coupon, setCoupon] = useState(null); // { code, discount_type, discount_value, discount, description }
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
      const c = localStorage.getItem(COUPON_KEY);
      if (c) setCoupon(JSON.parse(c));
    } catch (e) { console.warn("Cart hydrate failed", e); }
    setReady(true);
  }, []);

  useEffect(() => {
    if (ready) localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items, ready]);

  useEffect(() => {
    if (!ready) return;
    if (coupon) localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
    else localStorage.removeItem(COUPON_KEY);
  }, [coupon, ready]);

  const addItem = useCallback((product, quantity = 1, extras = {}) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.images?.[0],
          quantity,
          ...extras,
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(0, quantity) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    setCoupon(null);
  }, []);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  // Recompute discount from current subtotal (in case items changed)
  let discount = 0;
  if (coupon && subtotal > 0) {
    discount = coupon.discount_type === "percent"
      ? Math.min(subtotal, (subtotal * coupon.discount_value) / 100)
      : Math.min(subtotal, coupon.discount_value);
  }
  const discounted = Math.max(0, subtotal - discount);
  const shipping = items.length === 0 ? 0 : discounted >= 699 ? 0 : 60;
  const total = discounted + shipping;
  const count = items.reduce((s, i) => s + i.quantity, 0);

  const applyCoupon = useCallback(async (code) => {
    const sub = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const { data } = await api.post("/coupons/validate", { code, subtotal: sub });
    setCoupon(data);
    return data;
  }, [items]);

  const removeCoupon = useCallback(() => setCoupon(null), []);

  return (
    <CartContext.Provider value={{
      items, addItem, updateQuantity, removeItem, clear,
      subtotal, discount, shipping, total, count,
      coupon, applyCoupon, removeCoupon,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
